import { useEffect, useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  fetchBatchCurriculum,
  initBatchCurriculum,
  reorderBatchCurriculum,
  setBatchLessonVisibility,
  resetBatchCurriculum,
  addBatchLesson,
  updateBatchLesson,
  deleteBatchLesson,
} from "./adminCourseService";
import StudyMaterialsPanel from "./StudyMaterialsPanel";
import "./adminCourses.css";

const BLANK_LESSON = { title: "", learningObjective: "", durationMins: 30, isAssessment: false };

/* ── Inline lesson form (add / edit) ── */
const LessonForm = ({ initial, onSave, onCancel, saving }) => {
  const [f, setF] = useState(initial || BLANK_LESSON);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <tr style={{ background: "#f0f9ff" }}>
      <td />
      <td />
      <td>
        <input
          required
          value={f.title}
          onChange={(e) => set("title", e.target.value)}
          placeholder="Lesson title *"
          style={{ width: "100%", padding: "0.3rem", border: "1px solid #d1d5db", borderRadius: 4 }}
        />
        <input
          value={f.learningObjective}
          onChange={(e) => set("learningObjective", e.target.value)}
          placeholder="Learning objective"
          style={{ width: "100%", padding: "0.3rem", border: "1px solid #d1d5db", borderRadius: 4, marginTop: 4 }}
        />
      </td>
      <td>
        <input
          type="number" min={1}
          value={f.durationMins}
          onChange={(e) => set("durationMins", Number(e.target.value))}
          style={{ width: 64, padding: "0.3rem", border: "1px solid #d1d5db", borderRadius: 4 }}
        />
      </td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "0.82rem" }}>
            <input type="checkbox" checked={f.isAssessment} onChange={(e) => set("isAssessment", e.target.checked)} />
            Assessment
          </label>
          <button
            className="ac-btn ac-btn-success ac-btn-sm"
            disabled={saving || !f.title.trim()}
            onClick={() => onSave(f)}
          >
            {saving ? "…" : "Save"}
          </button>
          <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={onCancel}>Cancel</button>
        </div>
      </td>
    </tr>
  );
};

/* ── Sortable row ── */
const LessonRow = ({ lesson, dayNumber, onToggleVisibility, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: lesson.curriculumId });
  const [showMaterials, setShowMaterials] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : lesson.isHidden ? 0.45 : 1,
    background: isDragging ? "#eff6ff" : undefined,
  };

  return (
    <>
      <tr ref={setNodeRef} style={style}>
        <td
          {...attributes}
          {...listeners}
          style={{ cursor: "grab", textAlign: "center", color: "#9ca3af", userSelect: "none" }}
          title="Drag to reorder"
        >
          ⠿
        </td>
        <td>{dayNumber}</td>
        <td>
          {lesson.title}
          {lesson.isAssessment && (
            <span className="ac-badge ac-badge-purple" style={{ marginLeft: "0.4rem" }}>
              Assessment
            </span>
          )}
          {lesson.isHidden && (
            <span className="ac-badge ac-badge-gray" style={{ marginLeft: "0.4rem" }}>
              Hidden
            </span>
          )}
        </td>
        <td>{lesson.durationMins} min</td>
        <td>
          <div className="ac-actions">
            <button
              className="ac-btn ac-btn-ghost ac-btn-sm"
              onClick={() => setShowMaterials((p) => !p)}
            >
              📚 Materials
            </button>
            <button
              className={`ac-btn ac-btn-sm ${lesson.isHidden ? "ac-btn-success" : "ac-btn-ghost"}`}
              onClick={() => onToggleVisibility(lesson)}
            >
              {lesson.isHidden ? "Show" : "Hide"}
            </button>
            <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => onEdit(lesson)}>Edit</button>
            <button className="ac-btn ac-btn-danger ac-btn-sm" onClick={() => onDelete(lesson)}>Delete</button>
          </div>
        </td>
      </tr>
      {showMaterials && (
        <tr>
          <td colSpan={5} style={{ padding: "0 1rem 1rem 3rem", background: "#fafafa" }}>
            <StudyMaterialsPanel curriculumId={lesson.curriculumId} />
          </td>
        </tr>
      )}
    </>
  );
};

/* ── Main component ── */
const AdminBatchCurriculumTab = ({ batchId }) => {
  const [lessons, setLessons]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [initing, setIniting]     = useState(false);
  const [addingNew, setAddingNew] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [saving, setSaving]       = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchBatchCurriculum(batchId);
      setLessons(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load curriculum.");
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  const isCustomised = lessons.some((l) => l.isOverridden);

  /* ── Initialise override ── */
  const handleInit = async () => {
    setIniting(true);
    setError("");
    try {
      await initBatchCurriculum(batchId);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Initialisation failed.");
    } finally {
      setIniting(false);
    }
  };

  /* ── Drag end → reorder ── */
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = lessons.findIndex((l) => l.curriculumId === active.id);
    const newIndex = lessons.findIndex((l) => l.curriculumId === over.id);
    const reordered = arrayMove(lessons, oldIndex, newIndex);
    setLessons(reordered);

    try {
      await reorderBatchCurriculum(batchId, reordered.map((l) => l.curriculumId));
    } catch (err) {
      setError(err?.response?.data?.message || "Reorder failed. Refreshing…");
      load();
    }
  };

  /* ── Add batch-exclusive lesson ── */
  const handleAdd = async (data) => {
    setSaving(true);
    setError("");
    try {
      await addBatchLesson(batchId, data);
      setAddingNew(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Add failed.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Update batch-exclusive lesson ── */
  const handleUpdate = async (data) => {
    setSaving(true);
    setError("");
    try {
      await updateBatchLesson(batchId, editingLesson.curriculumId, data);
      setEditingLesson(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete batch-exclusive lesson ── */
  const handleDelete = async (lesson) => {
    if (!window.confirm(`Delete "${lesson.title}"?`)) return;
    setError("");
    try {
      await deleteBatchLesson(batchId, lesson.curriculumId);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Delete failed.");
    }
  };

  /* ── Toggle visibility ── */
  const handleToggleVisibility = async (lesson) => {
    setError("");
    try {
      const res = await setBatchLessonVisibility(batchId, lesson.curriculumId, !lesson.isHidden);
      setLessons((prev) =>
        prev.map((l) => l.curriculumId === lesson.curriculumId ? res.data : l)
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Visibility update failed.");
    }
  };

  /* ── Reset to course default ── */
  const handleReset = async () => {
    if (!window.confirm("Reset this batch to the course curriculum? All custom ordering and visibility changes will be lost.")) return;
    setError("");
    try {
      await resetBatchCurriculum(batchId);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Reset failed.");
    }
  };

  if (loading) return <div className="ac-loading">Loading curriculum…</div>;

  return (
    <div>
      {/* ── Error banner ── */}
      {error && (
        <div style={{
          background: "#fee2e2", color: "#b91c1c",
          padding: "0.6rem 0.9rem", borderRadius: 6,
          marginBottom: "1rem", fontSize: "0.88rem",
        }}>
          {error}
        </div>
      )}

      {/* ── INHERITED MODE ── */}
      {!isCustomised && (
        <>
          <div style={{
            background: "#eff6ff", color: "#1d4ed8",
            border: "1px solid #bfdbfe", borderRadius: 6,
            padding: "0.6rem 0.9rem", marginBottom: "1rem", fontSize: "0.88rem",
          }}>
            This batch is using the default course curriculum.
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <button
              className="ac-btn ac-btn-primary"
              onClick={handleInit}
              disabled={initing}
            >
              {initing ? "Initialising…" : "Customise This Batch"}
            </button>
          </div>

          {/* read-only table */}
          <div className="ac-table-wrap">
            <table className="ac-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Title</th>
                  <th>Duration</th>
                  <th>Assessment</th>
                </tr>
              </thead>
              <tbody>
                {lessons.length === 0 ? (
                  <tr><td colSpan={4} className="ac-empty">No lessons in curriculum.</td></tr>
                ) : (
                  lessons.map((l, idx) => (
                    <tr key={l.curriculumId}>
                      <td>{l.dayNumber ?? idx + 1}</td>
                      <td>{l.title}</td>
                      <td>{l.durationMins} min</td>
                      <td>
                        {l.isAssessment && (
                          <span className="ac-badge ac-badge-purple">Assessment</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── CUSTOMISED MODE ── */}
      {isCustomised && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.5rem" }}>
            <div style={{ fontWeight: 700, fontSize: "1rem", color: "#0f172a" }}>
              Batch Curriculum <span className="ac-badge ac-badge-blue" style={{ marginLeft: "0.4rem" }}>Customised</span>
            </div>
            <div className="ac-actions">
              <button
                className="ac-btn ac-btn-primary ac-btn-sm"
                onClick={() => { setAddingNew(true); setEditingLesson(null); }}
                disabled={addingNew}
              >
                + Add Lesson
              </button>
              <button className="ac-btn ac-btn-danger ac-btn-sm" onClick={handleReset}>
                Reset to Course Default
              </button>
            </div>
          </div>

          <div className="ac-table-wrap">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={lessons.map((l) => l.curriculumId)} strategy={verticalListSortingStrategy}>
                <table className="ac-table">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }} />
                      <th>Day</th>
                      <th>Title</th>
                      <th>Duration</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addingNew && (
                      <LessonForm
                        onSave={handleAdd}
                        onCancel={() => setAddingNew(false)}
                        saving={saving}
                      />
                    )}
                    {lessons.length === 0 && !addingNew ? (
                      <tr><td colSpan={5} className="ac-empty">No lessons.</td></tr>
                    ) : (
                      lessons.map((lesson, idx) =>
                        editingLesson?.curriculumId === lesson.curriculumId ? (
                          <LessonForm
                            key={lesson.curriculumId}
                            initial={editingLesson}
                            onSave={handleUpdate}
                            onCancel={() => setEditingLesson(null)}
                            saving={saving}
                          />
                        ) : (
                          <LessonRow
                            key={lesson.curriculumId}
                            lesson={lesson}
                            dayNumber={idx + 1}
                            onToggleVisibility={handleToggleVisibility}
                            onEdit={(l) => { setEditingLesson(l); setAddingNew(false); }}
                            onDelete={handleDelete}
                          />
                        )
                      )
                    )}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>

          <div style={{ marginTop: "0.75rem", fontSize: "0.8rem", color: "#9ca3af" }}>
            Drag rows to reorder. Hidden lessons are visible here but excluded for students.
          </div>
        </>
      )}
    </div>
  );
};

export default AdminBatchCurriculumTab;
